import { useQuery } from "@tanstack/react-query"
import { formatNumber } from "./format"
import { getLocale } from "./i18n"
import { hqApi } from "./medusa"

/**
 * The costs HQ puts on every order (backend lib/hq/cost-settings.ts), edited in Settings → Costs. The
 * screens that explain a cost in words read them from here, so the words always match the numbers.
 */
export type CostSettings = {
  site_box_now: number
  site_meetup: number
  site_home: number
  elta_prepaid: number
  elta_cod: number
  custom_box_now: number
  custom_meetup: number
  custom_home: number
  meetup_bag: number
  card_fee_percent: number
  card_fee_fixed: number
  paysafe_percent: number
  /** ¥ added to a supplier's price when HQ fills an item's cost (shipping inside China to Larry). */
  china_shipping_yuan: number
}

/** The owner's figures of 2026-09-25: shown until the saved ones arrive. */
export const DEFAULT_COSTS: CostSettings = {
  site_box_now: 5,
  site_meetup: 7,
  site_home: 10,
  elta_prepaid: 4.5,
  elta_cod: 6.5,
  custom_box_now: 5,
  custom_meetup: 7,
  custom_home: 5,
  meetup_bag: 0.75,
  card_fee_percent: 1.5,
  card_fee_fixed: 0.25,
  paysafe_percent: 5,
  china_shipping_yuan: 10,
}

/** A supplier's price with the shipping inside China added: what goes in an item's «Cost ¥». */
export const withChinaShipping = (yuan: number, costs: CostSettings) => Math.round((yuan + (costs.china_shipping_yuan ?? 10)) * 100) / 100

export function useCosts(): CostSettings {
  const query = useQuery({ queryKey: ["hq", "cost-settings"], queryFn: hqApi.costSettings, staleTime: 5 * 60_000 })
  return query.data?.settings ?? DEFAULT_COSTS
}

/** 5 → "€5", 0.75 → "€0.75", 6.5 → "€6.50" — in Greek 5 €, 0,75 €, 6,50 €. */
export const eur = (n: number) => {
  const figure = Number.isInteger(n) ? String(n) : n.toFixed(2)
  return getLocale() === "el" ? `${figure.replace(".", ",")} €` : `€${figure}`
}

export const cardFeeText = (c: CostSettings) => `${formatNumber(c.card_fee_percent)}% + ${eur(c.card_fee_fixed)}`
